import * as React from 'react'
import Lottie from 'react-lottie'
import { Text, StackChildren, Stack } from '@doordash/design-language'
import Loading from '@doordash/component-loading'
import styled from 'styled-components'
import { useCarousel } from '../carousel/context'
import useTimeout from '../../../hooks/useTimeout'

type Props = {
  jsonUrl: string
  title?: string
  subtitle?: string
  children?: React.ReactNode
  index: number
} & React.ComponentPropsWithoutRef<'div'>

const DEFAULT_ANIMATION_START_DELAY_MS = 900

const LoadingContainer = styled.div`
  display: flex;
  height: 50vh;
  align-items: center;
  justify-content: center;
`

/**
 * Slide component used for In App Education v4
 * @param jsonUrl URL to a JSON file to use as Lottie animation data
 * @param title String to use as title for slide
 * @param subtitle String to use as subtitle for slide
 * @param index Index of the slide w.r.t to adjacent children
 * @returns Slide component for Carousel
 **/
const Slide: React.FC<Props> = ({
  jsonUrl,
  title,
  subtitle,
  children,
  index,
}) => {
  const [animationAsset, setAnimationAsset] = React.useState<JSON>()
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [isStopped, setIsStopped] = React.useState<boolean>(true)
  const { state } = useCarousel()

  // Used to have a delay before the animation starts
  const startAnimation = () => setIsStopped(false)
  useTimeout(startAnimation, DEFAULT_ANIMATION_START_DELAY_MS)

  React.useEffect(() => {
    if (index === state.activeIndex) {
      fetch(jsonUrl)
        .then(response => response.json())
        .then(data => setAnimationAsset(data))
        .finally(() => setIsLoading(false))
    }
  }, [state.activeIndex, index, jsonUrl])

  if (isLoading) {
    return (
      <LoadingContainer>
        <Loading />
      </LoadingContainer>
    )
  }

  return (
    <figure style={{ margin: 0 }}>
      <Lottie
        options={{
          autoplay: true,
          loop: true,
          animationData: animationAsset,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice',
          },
        }}
        isStopped={isStopped}
        isClickToPauseDisabled={true}
        width={'70%'}
      />

      <figcaption>
        <StackChildren size={StackChildren.Sizes.None}>
          <Text styles={Text.Styles.Caption1} color={Text.Colors.TextTertiary}>
            {subtitle}
          </Text>
          <Text styles={Text.Styles.PageTitle2} color={Text.Colors.TextPrimary}>
            {title}
          </Text>
          <Stack size={Stack.Sizes.Medium} />
        </StackChildren>
        {children}
      </figcaption>
    </figure>
  )
}

export default Slide
